import { fmtMoney } from "../parsers/xmlParser";

// ── Colors from DashboardCharts ─────────────────────────────────────────────
const CAT_COLORS = {
  "ביטוח מנהלים": "#0d9488",
  "קרן פנסיה":    "#2563eb",
  "ביטוח חיים":   "#e11d48",
  "קופת גמל":     "#d97706",
  "קרן השתלמות":  "#16a34a",
  "קופת גמל להשקעה": "#7c3aed",
  "חיסכון לכל ילד": "#f43f5e",
};

const HON_KITZBA_COLORS = {
  "הון":                "#16a34a",
  "קצבה משלמת":         "#2563eb",
  "קצבה לא משלמת":      "#60a5fa",
};

const COMPANY_COLORS = [
  "#2563eb","#0d9488","#d97706","#7c3aed","#e11d48",
  "#0891b2","#16a34a","#94a3b8",
];

// ── Generic SVG Pie Chart ───────────────────────────────────────────────────
function GenericPieChart({ data, colors, width = 320, title }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return null;

  let currentAngle = 0;
  const cx = width / 2, cy = 75, r = 60;

  // Dynamic layout — column count derives from the WIDEST legend label so a long
  // "שם: סכום (אחוז%)" string never overflows into the neighbouring column.
  const isWide = width > 500;
  const legendChars = data.reduce((m, d) => {
    const nameLen = Math.min(d.name.length, 25);
    const valLen = `: ${fmtMoney(d.value)} (${Math.round((d.value / total) * 100)}%)`.length;
    return Math.max(m, nameLen + valLen);
  }, 0);
  const estLabelPx = legendChars * 6.4 + 28;            // ~px/char at fontSize 11 + swatch/padding
  const maxCols = isWide ? 3 : 2;
  const cols = Math.max(1, Math.min(maxCols, Math.floor((width - 20) / estLabelPx)));
  const colWidth = Math.floor((width - 20) / cols);

  // Calculate dynamic height based on legend items
  const numRows = Math.ceil(data.length / cols);
  const legendStartY = cy + r + 25;
  const dynamicHeight = legendStartY + (numRows * 22) + 15;

  const startX = isWide ? width - 20 : width - 30;

  return (
    <div style={{ flex: 1, minWidth: 280, textAlign: "center", border: "1px solid #f1f5f9", borderRadius: 12, padding: "12px 8px", background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", pageBreakInside: "avoid", breakInside: "avoid" }}>
      <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 10, color: "#1e3a8a", borderBottom: "1px solid #f1f5f9", paddingBottom: 6, width: "100%" }}>{title}</div>
      <svg width={width} height={dynamicHeight} style={{ direction: "rtl" }}>
        {/* Pie */}
        <g>
          {data.map((d, i) => {
            const angle = (d.value / total) * 360;
            const x1 = cx + r * Math.cos((currentAngle - 90) * Math.PI / 180);
            const y1 = cy + r * Math.sin((currentAngle - 90) * Math.PI / 180);
            currentAngle += angle;
            const x2 = cx + r * Math.cos((currentAngle - 90) * Math.PI / 180);
            const y2 = cy + r * Math.sin((currentAngle - 90) * Math.PI / 180);
            
            const largeArcFlag = angle > 180 ? 1 : 0;
            const pathData = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
            
            return (
              <path key={i} d={pathData} fill={colors[d.name] || colors[i % colors.length]} stroke="white" strokeWidth="1.5" />
            );
          })}
        </g>
        {/* Legend Underneath - One line per item */}
        <g transform={`translate(${startX}, ${legendStartY})`}>
          {data.map((d, i) => {
            const row = Math.floor(i / cols);
            const col = i % cols;
            const xOffset = col * -colWidth;
            const yOffset = row * 24; 
            const percentage = Math.round(d.value/total*100);
            const cleanName = d.name.length > 25 ? d.name.slice(0,22)+'...' : d.name;
            
            return (
              <g key={i} transform={`translate(${xOffset}, ${yOffset})`}>
                <rect width="12" height="12" fill={colors[d.name] || colors[i % colors.length]} rx="2" />
                <text x="-16" y="10" fontSize="11" fill="#1e293b" textAnchor="start">
                  <tspan fontWeight="800">{cleanName}: </tspan>
                  <tspan fontWeight="400" fill="#475569">{fmtMoney(d.value)} ({percentage}%)</tspan>
                </text>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}

const PRINT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Assistant:wght@300;400;600;700;800&display=swap');

  @page { size: A4; margin: 14mm 10mm; }
  body { font-family: 'Assistant', sans-serif; direction: rtl; color: #1e293b; line-height: 1.35; background: white; -webkit-print-color-adjust: exact; padding: 0; }
  .no-print { display: none; }
  @media print {
    .page-break { page-break-before: always; break-before: page; padding-top: 4mm; }
    .no-print { display: none; }
  }
  .header { display: flex; justify-content: space-between; align-items: top; margin-bottom: 15px; border-bottom: 2px solid #1e40af; padding-bottom: 6px; }
  .client-info h1 { margin: 0; color: #1e40af; font-size: 22px; font-weight: 800; letter-spacing: -0.5px; }
  .client-info p { margin: 1px 0; color: #64748b; font-size: 13px; }
  .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 12px; page-break-inside: avoid; break-inside: avoid; }
  .summary-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; text-align: center; }
  .summary-label { font-size: 11px; color: #64748b; margin-bottom: 2px; font-weight: 600; }
  .summary-value { font-size: 17px; font-weight: 800; color: #1e40af; }
  .section-title { font-size: 16px; font-weight: 800; color: #1e3a8a; border-right: 4px solid #1e40af; padding-right: 10px; margin: 15px 0 8px; letter-spacing: -0.3px; page-break-after: avoid; break-after: avoid; }
  .global-comment { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; padding: 12px; margin-bottom: 12px; font-size: 13px; white-space: pre-wrap; line-height: 1.4; page-break-inside: avoid; break-inside: avoid; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 15px; table-layout: fixed; }
  th { background: #f1f5f9; padding: 8px; text-align: right; border-bottom: 2px solid #cbd5e1; font-weight: 800; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; }
  td { padding: 6px 10px; border-bottom: 1px solid #e2e8f0; vertical-align: top; overflow: hidden; }
  tr { page-break-inside: avoid; break-inside: avoid; }
  .fund-name { font-weight: 700; color: #1e293b; font-size: 11px; }
  .recommendation { background: #fffbeb; padding: 6px 10px; border-radius: 8px; font-size: 11px; margin-top: 4px; border: 1px solid #fef3c7; color: #92400e; font-style: italic; white-space: pre-wrap; line-height: 1.35; }
  .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 8px; page-break-inside: avoid; break-inside: avoid; }
  .hk-breakdown { font-size: 10px; color: #475569; margin-top: 4px; padding-top: 4px; border-top: 1px dashed #e2e8f0; line-height: 1.35; }
  svg text { font-family: 'Assistant', sans-serif !important; }
  .chart-container { margin-bottom: 8px; }
`;

export default function CustomerSnapshotReport({ funds, snapshot, clientName, logo, totals, hideRecommendations = false }) {
  const reportDate = new Date().toLocaleDateString("he-IL", { day: "numeric", month: "long", year: "numeric" });

  const validDate = funds
    .map(f => f.validDate)
    .filter(Boolean)
    .sort((a, b) => {
      const parse = d => { const [day, m, y] = d.split("/"); return new Date(y, m - 1, day); };
      return parse(a) - parse(b);
    })[0] || "";

  // 1. Data for Category Chart
  const catMap = {};
  funds.forEach(f => catMap[f.product] = (catMap[f.product] || 0) + f.redeem);
  const catData = Object.entries(catMap).map(([name, value]) => ({ name, value })).sort((a,b) => b.value - a.value);

  // 2. Data for Hon/Kitzba Chart
  const hkMap = {};
  funds.forEach(f => hkMap[f.type] = (hkMap[f.type] || 0) + f.redeem);
  const hkData = Object.entries(hkMap).map(([name, value]) => ({ name, value }));

  // 3. Data for Company Chart
  // שמות חברה ארוכים גולשים על עמודות הליגנדה — דוחפים אותם לסוף (בין עצמם עדיין לפי סכום),
  // כך שיופיעו בשורות האחרונות ולא ישברו את הפריסה הרב-עמודתית.
  const LONG_COMPANY_NAME = 16;
  const compMap = {};
  funds.forEach(f => compMap[f.company] = (compMap[f.company] || 0) + f.redeem);
  const compData = Object.entries(compMap)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => {
      const aLong = a.name.length > LONG_COMPANY_NAME ? 1 : 0;
      const bLong = b.name.length > LONG_COMPANY_NAME ? 1 : 0;
      if (aLong !== bLong) return aLong - bLong;  // ארוכים לסוף
      return b.value - a.value;                    // אחרת לפי סכום יורד
    });

  return (
    <div className="report-root">
      <style dangerouslySetInnerHTML={{ __html: PRINT_CSS }} />
      
      {/* ══ PAGE 1: CHARTS & SUMMARY ═════════════════════════════════════ */}
      <div className="header">
        <div className="client-info">
          <h1>תמונת מצב פיננסית ופנסיונית</h1>
          <p>מוכן עבור: <strong>{clientName}</strong></p>
          <p>תאריך הפקה: {reportDate}{validDate && <> — נכונות הנתונים: <strong>{validDate}</strong></>}</p>
        </div>
        {logo && <img src={logo} alt="לוגו" style={{ height: 80, maxWidth: 220, objectFit: "contain" }} />}
      </div>

      <div className="summary-grid">
        <div className="summary-card">
          <div className="summary-label">סה"כ נכסים מנוהלים</div>
          <div className="summary-value">{fmtMoney(totals.redeem)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">קצבה חודשית צפויה</div>
          <div className="summary-value">{fmtMoney(totals.pension)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">כיסוי ביטוח חיים (מירבי)</div>
          <div className="summary-value">{fmtMoney(totals.life)}</div>
        </div>
      </div>

      <div className="section-title">ניתוח חלוקת נכסים ותמיל תיק</div>
      <div className="chart-container" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginTop: 6 }}>
        <GenericPieChart title="סוגי מוצרים" data={catData} colors={CAT_COLORS} />
        <GenericPieChart title="הון וקצבה" data={hkData} colors={HON_KITZBA_COLORS} />
      </div>
      <div className="chart-container" style={{ marginTop: 10, display: "flex", justifyContent: "center" }}>
        <GenericPieChart title="חלוקה לפי חברה מנהלת" data={compData} colors={COMPANY_COLORS} width={660} />
      </div>

      {snapshot.globalComment && (
        <>
          <div className="section-title">תמצית מנהלים והמלצות אסטרטגיות</div>
          <div className="global-comment">{snapshot.globalComment}</div>
        </>
      )}

      {/* ══ PAGE 2: ASSET DETAILS ═══════════════════════════════════════ */}
      <div className="page-break">
        <div className="section-title">פירוט נכסים והמלצות פרטניות</div>
        <table>
          <thead>
            <tr>
              <th style={{ width: "22%" }}>חברה ומוצר</th>
              <th style={{ width: "24%" }}>יתרה וסיווג מפורט</th>
              <th style={{ width: "20%" }}>מסלולי השקעה</th>
              <th style={{ width: "16%" }}>הפקדה אחרונה</th>
              <th style={{ width: "18%" }}>קצבה חודשית</th>
            </tr>
          </thead>
          <tbody>
            {funds.map((f, i) => ([
              <tr key={i}>
                <td>
                  <div className="fund-name">{f.company}</div>
                  <div style={{ fontSize: 9 }}>{f.product}</div>
                  <div style={{ fontSize: 9, color: "#64748b" }}>{f.tochnit}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#334155", marginTop: 1 }}>{f.polNum}</div>
                  <div style={{ fontSize: 8, marginTop: 2 }}>
                    <span style={{
                      padding: "1px 5px", borderRadius: 8, fontWeight: 700,
                      background: f.statusCode === "1" ? "#dcfce7" : "#fee2e2",
                      color:      f.statusCode === "1" ? "#166534" : "#991b1b",
                    }}>{f.statusLabel}</span>
                    {f.statusCode === "1" && f.employerName && f.employerName !== "—" && (
                      <span style={{ color: "#64748b", marginRight: 4 }}> | {f.employerName}</span>
                    )}
                  </div>
                </td>
                <td>
                  <div style={{ fontWeight: 800, fontSize: 13 }}>{fmtMoney(f.redeem)}</div>
                  <div className="hk-breakdown">
                     <div style={{ fontWeight: 700, marginBottom: 2 }}>פירוט רכיבים:</div>
                     {f.hk.hon > 0 && <div>• תגמולים (הון): {fmtMoney(f.hk.hon)}</div>}
                     {f.hk.kitzba > 0 && <div>• תגמולים (קצבה): {fmtMoney(f.hk.kitzba)}</div>}
                     {f.hk.until2000 > 0 && <div>• תגמולים (עד 2000): {fmtMoney(f.hk.until2000)}</div>}
                     {f.hk.honP > 0 && <div style={{ color: "#b91c1c" }}>• פיצויים (הון): {fmtMoney(f.hk.honP)}</div>}
                     {f.hk.kitzbaP > 0 && <div style={{ color: "#b91c1c" }}>• פיצויים (קצבה): {fmtMoney(f.hk.kitzbaP)}</div>}
                  </div>
                  {(f.dmeiTzv > 0 || f.dmeiHaf > 0) && (
                    <div style={{ fontSize: 9, color: "#475569", marginTop: 4, borderTop: "1px dashed #e2e8f0", paddingTop: 3 }}>
                      דמ"צ: <strong>{f.dmeiTzv}%</strong>
                      {f.dmeiHaf > 0 && <>, דמ"ה: <strong>{f.dmeiHaf}%</strong></>}
                    </div>
                  )}
                </td>
                <td>
                  {(f.groupedMaslulim || []).map((m, i) => (
                    <div key={i} style={{ fontSize: 9, display: "flex", justifyContent: "space-between", gap: 4 }}>
                      <span>{m.name}</span>
                      <strong>{fmtMoney(m.balance)}</strong>
                    </div>
                  ))}
                </td>
                <td>
                  {f.lastDepAmt > 0 ? (
                    <>
                      <div style={{ fontWeight: 700 }}>{fmtMoney(f.lastDepAmt)}</div>
                      <div style={{ fontSize: 9, color: "#64748b" }}>{f.lastDepDate}</div>
                      {f.lastDepEmployer !== "—" && (
                        <div style={{ fontSize: 8, color: "#94a3b8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.lastDepEmployer}</div>
                      )}
                    </>
                  ) : <span style={{ color: "#94a3b8" }}>—</span>}
                </td>
                <td>
                  {f.pension > 0 ? (
                    <>
                      <div style={{ fontWeight: 700, color: "#16a34a", fontSize: 11 }}>{fmtMoney(f.pension)}/חודש</div>
                      {f.isMeshalemet && <div style={{ fontSize: 9, color: "#15803d" }}>קופה משלמת</div>}
                      {f.mekadem > 0 && <div style={{ fontSize: 9, color: "#64748b" }}>מקדם: {f.mekadem.toLocaleString("he-IL", { maximumFractionDigits: 2 })}</div>}
                    </>
                  ) : <span style={{ color: "#94a3b8" }}>—</span>}
                </td>
              </tr>,
              (!hideRecommendations && snapshot.fundComments[f.id]) ? (
                <tr key={`${i}-rec`}>
                  <td colSpan={5} style={{ paddingTop: 0 }}>
                    <div className="recommendation"><strong>💬 המלצת יועץ:</strong> {snapshot.fundComments[f.id]}</div>
                  </td>
                </tr>
              ) : null
            ])).flat()}
          </tbody>
        </table>
      </div>

      {/* ══ PAGE 3: BENEFICIARIES ════════════════════════════════════════ */}
      <div className="page-break">
        <div className="section-title">ריכוז מוטבים וחלוקת עיזבון</div>
        <p style={{ fontSize: 11, marginBottom: 15, color: "#475569" }}>
          להלן ריכוז המוטבים כפי שהם מעודכנים במערכות של כל גוף מוסדי. מומלץ לוודא שהחלוקה תואמת את רצון הלקוח והצוואה הקיימת.
        </p>
        
        <table style={{ fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{ width: "30%" }}>שם הקופה / פוליסה</th>
              <th style={{ width: "70%" }}>מוטבים רשומים ואחוז חלוקה</th>
            </tr>
          </thead>
          <tbody>
            {funds.map((f, i) => (
              <tr key={i}>
                <td style={{ fontWeight: 700 }}>
                  {f.company} - {f.product}
                  <div style={{ fontWeight: 400, fontSize: 9, color: "#64748b" }}>#{f.polNum}</div>
                </td>
                <td>
                  {f.mutavim && f.mutavim.length > 0 ? (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "5px 15px" }}>
                      {f.mutavim.map((m, j) => (
                        <div key={j} style={{ borderBottom: "1px dashed #e2e8f0", paddingBottom: 2 }}>
                          <strong>{m.displayName}</strong> - {m.pct ? `${m.pct}%` : "—"}
                          <div style={{ fontSize: 9, color: "#64748b" }}>{m.zika || ""} {m.id ? `(${m.id})` : ""}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: "#dc2626", fontWeight: 700 }}>לא הוגדרו מוטבים / לפי תקנון</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="footer">
        <p>דוח זה הופק באמצעות מערכת התכנון הפנסיוני ומהווה כלי עזר לקבלת החלטות. אין לראות בדוח זה ייעוץ משפטי או תחליף לייעוץ פרטני.</p>
      </div>
    </div>
  );
}
